import { Translate, TranslationsContext } from '@teliaee/telia-translate';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Card, CardContent, EqualHeightProvider, Grid, GridColumn, Separator, Text } from 'telia-front-react';
import { v4 } from 'uuid';
import SpinnerWithSpacing from '../components/SpinnerWithSpacing';
import Config from '../Config';
import { Product } from '../content/ProductInterfaces';
import SearchStore, { CATEGORY_ARCHIVED, ESHOP_CATEGORY, ESHOP_CATEGORY_ARCHIVED } from '../stores/SearchStore';
import Telia1Store from '../stores/Telia1Store';
import ProductCardWrapper from './ProductCardWrapper';
import SearchErrorNotice from './SearchErrorNotice';
import { AllAndArchivedFilterOrder, SearchEvent } from './SearchInterfaces';
import SearchNotFoundNotice from './SearchNotFoundNotice';
import SearchNotFoundResults from './SearchNotFoundResults';
import ShowMoreButton from './ShowMoreButton';
import SwitchArchiveFilterButton from './SwitchArchiveFilterButton';

export const PRODUCT_STATUS_ARCHIVED = 'archived';

interface EShopSearchResultsProps {
  searchStore?: SearchStore;
  telia1Store?: Telia1Store;
  isOnlyEshopResults?: boolean;
  isMobile?: boolean;
}

@observer
class EShopSearchResults extends React.Component<EShopSearchResultsProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  componentDidMount() {
    this.props.telia1Store.loadTelia1();
  }

  public render() {
    const { searchStore, telia1Store } = this.props;
    const translate = this.context.translate;
    if (searchStore.searchWrappers[ESHOP_CATEGORY].status === 'UNINITIALIZED') {
      return null;
    }
    if (searchStore.searchWrappers[ESHOP_CATEGORY].status === 'FETCHING' || telia1Store.isFetching) {
      return <SpinnerWithSpacing />;
    }
    return (
      <Text>
        <Card background="transparent" padding="none">
          <CardContent>
            {this.renderProductsBlock()}
            <SwitchArchiveFilterButton category={ESHOP_CATEGORY} labelPrefix="shop" />
            <Separator spacing="small" color="invisible" />
            {this.props.isOnlyEshopResults && this.renderSearchRedirects(translate)}
          </CardContent>
        </Card>
      </Text>
    );
  }

  private renderSearchRedirects = (translate: Translate) => {
    const searchStore = this.props.searchStore;
    return (
      <p className="text-center">
        {translate('search.entire.telia', {
          teliaWebUrl: Config.teliaWebUrl,
          searchWord: searchStore.query,
          searchWordUrl: searchStore.query,
          secondTeliaWebUrl: Config.teliaWebUrl,
          secondSearchWord: searchStore.query
        })}
      </p>
    );
  };

  private renderProductsBlock() {
    const { searchStore } = this.props;
    const translate = this.context.translate;

    const searchWrapperCategory =
      searchStore.searchWrappers[ESHOP_CATEGORY].filterOrder === AllAndArchivedFilterOrder.ARCHIVED ? ESHOP_CATEGORY_ARCHIVED : ESHOP_CATEGORY;
    const searchWrapper = searchStore.searchWrappers[searchWrapperCategory];
    if (!searchWrapper.total && !['ERROR', 'FETCH_MORE_ERROR', 'REFETCHING'].includes(searchWrapper.status)) {
      return searchStore.isOnlyShopSearch ? (
        <SearchNotFoundResults onlyShopSearch={true} isMobile={this.props.isMobile} />
      ) : (
        <SearchNotFoundNotice customType={'eshop'} />
      );
    }
    return (
      <>
        {this.displayResults(searchWrapperCategory)}
        <SearchErrorNotice showNoticeWhen={['ERROR', 'FETCH_MORE_ERROR']} status={searchWrapper.status} translate={translate} />
        <Separator color="invisible" spacing="xsmall" />
        <ShowMoreButton category={searchWrapperCategory} />
      </>
    );
  }

  private displayResults(searchWrapperCategory: string): JSX.Element {
    const { searchStore, telia1Store } = this.props;
    const searchWrapper = searchStore.searchWrappers[searchWrapperCategory];
    const archived: boolean = searchWrapperCategory.includes(CATEGORY_ARCHIVED);
    if (searchWrapper.status === 'REFETCHING' || telia1Store.isFetching) {
      return <SpinnerWithSpacing spacing={'small'} />;
    }
    if (searchWrapper.status === 'FETCHED' && !searchWrapper.results.length) {
      return searchStore.isOnlyShopSearch ? (
        <SearchNotFoundResults onlyShopSearch={true} isMobile={this.props.isMobile} />
      ) : (
        <SearchNotFoundNotice customType={`eshop${archived ? '-archived' : ''}`} />
      );
    }

    return (
      <EqualHeightProvider
        groups={['product-card-colors', 'product-card-title', 'product-card-footer-left', 'product-card-footer-right', 'product-card-footer-button']}
      >
        <Grid equalHeight={true}>
          {toJS(searchWrapper.results).map((product, idx) => {
            return (
              <GridColumn key={`eshop-${idx}-'grid'`} width={['xs-6', 'lg-3']}>
                <ProductCardWrapper
                  key={`product-card-${product.ean}`}
                  product={product}
                  urlParam={btoa(
                    JSON.stringify(this.getEventAnalyticsData(product, archived, { eventName: 'Shop search add to cart', eventType: 'conversion' }))
                  )}
                  onClickCallback={() => this.sendAlgoliaAnalyticsData(product, archived)}
                  hasTelia1={telia1Store.hasTelia1}
                />
              </GridColumn>
            );
          })}
        </Grid>
      </EqualHeightProvider>
    );
  }

  private sendAlgoliaAnalyticsData = async (product: Product, archived: boolean) => {
    const { searchStore } = this.props;
    const category = archived ? ESHOP_CATEGORY_ARCHIVED : ESHOP_CATEGORY;
    const positions = [searchStore.searchWrappers[category].results.findIndex((p) => p.productCode === product.productCode) + 1];
    const searchEvent: SearchEvent = this.getEventAnalyticsData(product, archived, { positions });

    searchStore.postSearchTabAnalytics();
    searchStore.postSearchResultClickEvent(searchEvent);
  };

  private getEventAnalyticsData = (product: Product, archived: boolean, data?: Partial<SearchEvent>): SearchEvent => {
    const { searchStore } = this.props;
    const category = archived ? ESHOP_CATEGORY_ARCHIVED : ESHOP_CATEGORY;
    const sortOrder = searchStore.getEShopProductSortOrder().toLowerCase();
    const idxName = `${Config.componentTier}_shop${archived ? '-archive' : ''}${sortOrder === 'relevance' ? '' : `_${sortOrder}`}`;
    return {
      eventName: 'Shop search result click',
      eventType: 'click',
      index: idxName,
      objectIDs: [product.productCode],
      userToken: v4(),
      queryID: searchStore.searchWrappers[category].queryId,
      ...data
    };
  };
}

export default inject('searchStore', 'telia1Store')(EShopSearchResults);
